import { useCallback, useEffect, useRef, useState } from "react";
import { closeBlitzSession, fetchStudyQueue } from "../../service/BlitzReportingService";
import blitzSessionContext from "./blitzSessionContext";
import { getEnv } from '../../helpers/EnvHelper';

const BlitzSessionState = (props) => {
  const [allStudies, setAllStudies] = useState([]);
  const [caseQueue, setCaseQueue] = useState([]);
  const [processedQueue, setProcessedQueue] = useState([]);
  const [currentStudy, setCurrentStudy] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [showCloseBlitzPopup, setShowCloseBlitzPopup] = useState(false);
  const [showEmptyQueuePopup, setShowEmptyQueuePopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState("right");
  const [previousCase, setPreviousCase] = useState(null);

  const iframe0Ref = useRef(null);
  const iframe1Ref = useRef(null);
  const iframe2Ref = useRef(null);
  const iframe3Ref = useRef(null);
  const iframeRefs = [iframe0Ref, iframe1Ref, iframe2Ref, iframe3Ref];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleIframe, setVisibleIframe] = useState(0);
  const [iframeOrder, setIframeOrder] = useState([-1, -1, -1, -1]);
  const [loadingStates, setLoadingStates] = useState({
    0: false,
    1: false,
    2: false,
    3: false,
  });

  const [firstStudyLoaded, setFirstStudyLoaded] = useState(false);
  const [firstStudyFullyLoaded, setFirstStudyFullyLoaded] = useState(false);
  const [showCaseRemovedPopup, setShowCaseRemovedPopup] = useState(false);
  const [removedCaseInfo, setRemovedCaseInfo] = useState(null);

  const [loadingQueue, setLoadingQueue] = useState([]);
  const [currentlyLoading, setCurrentlyLoading] = useState(null);
  const [studyCompletionStatus, setStudyCompletionStatus] = useState({});

  const [loadingAttempts, setLoadingAttempts] = useState({});
  const [failedStudies, setFailedStudies] = useState(new Set());
  
  const [prefetchStatusMap, setPrefetchStatusMap] = useState({});

  const params = new URLSearchParams(window.location.search);
  const [studyBlitzFilter, setStudyBlitzFilter] = useState(params.get("filter"));
  const MAX_LOADING_ATTEMPTS = 3;

  const caseQueueRef = useRef([]);
  const processedQueueRef = useRef([]);
  const currentStudyRef = useRef(null);
  const sessionIdRef = useRef(null);
  const iframeOrderRef = useRef([-1, -1, -1, -1]);
  const visibleIframeRef = useRef(0);
  const loadingStatesRef = useRef({ 0: false, 1: false, 2: false, 3: false });
  const isPreloadingRef = useRef(false);
  const isQueueRefreshingRef = useRef(false);
  const prefetchStatusMapRef = useRef(prefetchStatusMap);
  const isSessionClosedRef = useRef(false);

  useEffect(() => {
    caseQueueRef.current = caseQueue;
  }, [caseQueue]);

  useEffect(() => {
    processedQueueRef.current = processedQueue;
  }, [processedQueue]);

  useEffect(() => {
    currentStudyRef.current = currentStudy;
  }, [currentStudy]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    iframeOrderRef.current = iframeOrder;
  }, [iframeOrder]);

  useEffect(() => {
    visibleIframeRef.current = visibleIframe;
  }, [visibleIframe]);

  useEffect(() => {
    loadingStatesRef.current = loadingStates;
  }, [loadingStates]);

  useEffect(() => {
    prefetchStatusMapRef.current = prefetchStatusMap;
  }, [prefetchStatusMap]);

  useEffect(() => {
    loadInitialQueue();
  }, [studyBlitzFilter]);

  useEffect(() => {
    console.log("session id", sessionId);
    if (!sessionId) return;
    
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("sessionId", sessionId);
    window.history.replaceState({}, "", currentUrl.toString());

    const handleWindowClose = (e) => {
      logBlitzSessionClose("WINDOW_CLOSE");
    };

    window.addEventListener("beforeunload", handleWindowClose);
    return () => {
      window.removeEventListener("beforeunload", handleWindowClose);
    };
  }, [sessionId]);

  useEffect(() => {
    if (currentStudy && !firstStudyLoaded) {
      console.log("Loading first study:", currentStudy.studyId);
      setCurrentlyLoading(currentStudy.studyId);
      loadStudyInIframe(0, currentStudy.studyId);
      setIframeOrder([currentStudy.studyId, -1, -1, -1]);
      setVisibleIframe(0);
      setFirstStudyLoaded(true);
    }
  }, [currentStudy]);

  useEffect(() => {
    const preloadInterval = setInterval(() => {
      preloadNextStudies();
    }, 3000);

    return () => {
      clearInterval(preloadInterval);
    };
  }, []);

  useEffect(() => {
    const queueRefreshInterval = setInterval(() => {
      refreshQueueFromBackend();
    }, 30000);

    return () => {
      clearInterval(queueRefreshInterval);
    };
  }, []);

  const preloadNextStudies = useCallback(() => {
    const currentQueue = caseQueueRef.current;
    if (!currentQueue || currentQueue.length === 0) {
      console.log("No studies to preload");
      return;
    }

    // Get next studies that need to be loaded (up to 3)
    const studiesToPreload = currentQueue
      .slice(0, 3)
      .map((study) => study.studyId)
      .filter(
        (studyId) =>
          !iframeOrderRef.current.includes(studyId) &&
          !studyCompletionStatus[studyId] &&
          !failedStudies.has(studyId) &&
          (loadingAttempts[studyId] || 0) < MAX_LOADING_ATTEMPTS
      );

    if (studiesToPreload.length === 0) {
      console.log("All next 3 studies already preloaded or failed max attempts");
      return;
    }

    console.log("Studies available for sequential loading:", studiesToPreload);

    setLoadingQueue((prev) => {
      const newQueue = [...prev];
      studiesToPreload.forEach((studyId) => {
        if (
          !newQueue.includes(studyId) &&
          !failedStudies.has(studyId) &&
          (loadingAttempts[studyId] || 0) < MAX_LOADING_ATTEMPTS
        ) {
          newQueue.push(studyId);
          updatePrefetchStatus(studyId, "pending");
        }
      });
      return newQueue;
    });
  }, [studyCompletionStatus, loadingAttempts, failedStudies]);

  const refreshQueueFromBackend = async () => {
    if (isQueueRefreshingRef.current) {
      console.log("Queue refresh already in progress, skipping");
      return;
    }

    isQueueRefreshingRef.current = true;

    try {
      console.log("Refreshing queue from backend...");
      const response = await fetchStudyQueue(studyBlitzFilter, sessionIdRef.current);

      if (response.status === 200 && response.data.result === "SUCCESS") {
        const freshQueue = response.data.data.cases;
        const currentStudyId = currentStudyRef.current?.studyId;
        const oldQueue = caseQueueRef.current;
        const processedStudyIds = processedQueueRef.current?.map(
          (study) => study.studyId
        );

        const isCurrentCaseRemoved =
          currentStudyId &&
          !freshQueue.some((study) => study.studyId === currentStudyId);
        const newQueue = filterCurrentIframe(freshQueue, currentStudyId);
        const filteredQueue = filterProcessedStudies(
          newQueue,
          processedStudyIds
        );

        copyIframeHandles(oldQueue, filteredQueue);

        const queueWithPreservedStatus = filteredQueue.map((newStudy) => {
          const existingStatus = prefetchStatusMapRef.current[newStudy.studyId] || "not_started";
          return { ...newStudy, prefetchStatus: existingStatus,};
        });

        setCaseQueue(queueWithPreservedStatus);

        if (isCurrentCaseRemoved) {
          console.log("Processing current iframe removal");
          processCurrentIframeRemoval(currentStudyId);
        }
      } else {
        console.error("Failed to fetch queue:", response);
      }
    } catch (error) {
      console.error("Error refreshing queue:", error);
    } finally {
      isQueueRefreshingRef.current = false;
    }
  };

  const filterCurrentIframe = (queue, currentStudyId) => {
    if (!currentStudyId) return queue;
    return queue.filter((study) => study.studyId !== currentStudyId);
  };

  const filterProcessedStudies = (queue, processedStudyIds) => {
    if (!processedStudyIds || processedStudyIds.length === 0) return queue;
    return queue.filter((study) => !processedStudyIds.includes(study.studyId));
  };

  const copyIframeHandles = (oldQueue, newQueue) => {
    if (!oldQueue || !newQueue) return;

    const oldTop3 = oldQueue.slice(0, 3);
    const newTop3 = newQueue.slice(0, 3);

    const oldTop3StudyIds = oldTop3.map((study) => study.studyId);
    const newTop3StudyIds = newTop3.map((study) => study.studyId);
    console.log("Old top 3:"+ oldTop3StudyIds +"New top 3:" + newTop3StudyIds);

    const studiesToDestroy = oldTop3.filter((oldStudy) => {
        if (!oldStudy.studyId) return false;

        const matchingNew = newTop3.find((s) => s.studyId === oldStudy.studyId);

        return (
          !matchingNew ||
          (matchingNew.updatedAt && matchingNew.updatedAt !== oldStudy.updatedAt)
        );
      }).map((s) => s.studyId);

    studiesToDestroy.forEach((studyId) => {
      destroyIframe(studyId);
    });
  };

  const destroyIframe = (studyId) => {
    if (!studyId) return;

    setIframeOrder((prevOrder) => {
      const newOrder = [...prevOrder];
      const iframeIndex = newOrder.findIndex((id) => id === studyId);

      if (iframeIndex !== -1 && iframeIndex !== visibleIframeRef.current) {
        console.log(`Destroying iframe for study ${studyId} at index ${iframeIndex}`);
        if (iframeRefs[iframeIndex].current) {
          iframeRefs[iframeIndex].current.src = "about:blank";
        }

        newOrder[iframeIndex] = -1;
        setLoadingStates((prev) => ({ ...prev, [iframeIndex]: false }));

        updatePrefetchStatus(studyId, "not_started");
      } else if (iframeIndex === visibleIframeRef.current) {
        console.log(`Cannot destroy visible iframe for study ${studyId}`);
      }

      return newOrder;
    });
  };

  const processCurrentIframeRemoval = (removedStudyId) => {
    const currentStudyInfo = currentStudyRef.current;

    if (!currentStudyInfo) {
      console.log("No current study to process removal for");
      return;
    }

    const currentOrder = iframeOrderRef.current;
    const removedIframeIndex = currentOrder.findIndex(
      (id) => id === removedStudyId
    );
    if (removedIframeIndex !== -1) {
      console.log(`Clearing removed study ${removedStudyId} from iframe ${removedIframeIndex}`);

      setIframeOrder((prev) => {
        const newOrder = [...prev];
        newOrder[removedIframeIndex] = -1;
        return newOrder;
      });

      if (iframeRefs[removedIframeIndex].current) {
        iframeRefs[removedIframeIndex].current.src = "about:blank";
      }

      setLoadingStates((prev) => ({ ...prev, [removedIframeIndex]: false }));
    }

    setLoadingQueue((prev) => prev.filter((studyId) => studyId !== removedStudyId));
    setCurrentlyLoading((prev) => (prev === removedStudyId ? null : prev));
    setLoadingAttempts((prev) => {
      const newAttempts = { ...prev };
      delete newAttempts[removedStudyId];
      return newAttempts;
    });

    setFailedStudies((prev) => {
      const newFailed = new Set(prev);
      newFailed.delete(removedStudyId);
      return newFailed;
    });

    setRemovedCaseInfo(currentStudyInfo);
    setShowCaseRemovedPopup(true);

    // setTimeout(() => {
    //   handleCloseRemovedCasePopup();
    // }, 10000);
  };

  const handleCloseRemovedCasePopup = () => {
    console.log("Closing removed case popup");
    setShowCaseRemovedPopup(false);
    setRemovedCaseInfo(null);
    goToNextStudy(false);
  };

  const loadInitialQueue = async () => {
    const response = await fetchStudyQueue(studyBlitzFilter, sessionId);
    if (response.status === 200 && response.data.result === "SUCCESS") {
      const cases = response.data.data.cases;
      setSessionId(response.data.data.sessionId);

      console.log("Loaded initial queue:", cases.map((c) => c.studyId));

      const casesWithStatus = cases.map((study) => ({
        ...study,
        prefetchStatus: "not_started",
      }));

      const initialStatusMap = {};
      casesWithStatus.forEach((study) => {
        initialStatusMap[study.studyId] = "not_started";
      });
      setPrefetchStatusMap(initialStatusMap);
      setAllStudies(casesWithStatus);

      if (casesWithStatus.length > 0) {
        setCurrentStudy(casesWithStatus[0]);
        setCaseQueue(casesWithStatus.slice(1));
      }else{
        setShowEmptyQueuePopup(true);
      }
    }
  };

  const updatePrefetchStatus = useCallback((studyId, newStatus) => {
    setPrefetchStatusMap((prev) => ({ ...prev, [studyId]: newStatus }));
    setCurrentStudy((prev) =>
      prev?.studyId === studyId ? { ...prev, prefetchStatus: newStatus } : prev
    );
    setCaseQueue((prev) =>
      prev.map((study) =>
        study.studyId === studyId
          ? { ...study, prefetchStatus: newStatus }
          : study
      )
    );
    setProcessedQueue((prev) =>
      prev.map((study) =>
        study.studyId === studyId
          ? { ...study, prefetchStatus: newStatus }
          : study
      )
    );
    setAllStudies((prev) =>
      prev.map((study) =>
        study.studyId === studyId
          ? { ...study, prefetchStatus: newStatus }
          : study
      )
    );
  }, []);

  const loadStudyInIframe = (iframeIndex, studyId, switchVisibleNow = false) => {
    console.log("Loading study in iframe", iframeIndex, "->", studyId);
    const url = getStudyUrl(studyId);
    
    if (url && iframeRefs[iframeIndex].current) {
      setLoadingStates((prev) => ({ ...prev, [iframeIndex]: true }));
      updatePrefetchStatus(studyId, "loading");
      if (switchVisibleNow) {
        setVisibleIframe(iframeIndex);
      }
      iframeRefs[iframeIndex].current.src = url;
    }
  };

  const getStudyUrl = (studyId) => {
    let study = allStudies.find((s) => s.studyId === studyId);
    if (!study) {
      const currentQueueStudies = [
        currentStudy,
        ...processedQueue,
        ...caseQueue,
      ].filter(Boolean);
      study = currentQueueStudies.find((s) => s.studyId === studyId);
    }

    if (!study) {
      console.warn(`Study not found for ID: ${studyId}`);
      return null;
    }

    if (study?.priorsCount && study?.priorsCount > 0 && study?.showPriors && getEnv("REACT_APP_SHOW_PRIORS") == "true") {
      return `${window.location.origin}/new/study/priors/load/${studyId}?firmId=${study.firmId}&blitzSession=${sessionIdRef.current}`
    }

    return `${window.location.origin}/new/study/load/${studyId}?firmId=${study.firmId}&blitzSession=${sessionIdRef.current}`;
  };

  const goToNextStudy = (moveToProcessed = true) => {
    console.log("Go to next study", { moveToProcessed });
    if (!caseQueueRef.current || caseQueueRef.current.length === 0) {
      console.log("No more studies in queue");
      setShowEmptyQueuePopup(true);
      return;
    }

    const previousStudy = currentStudyRef.current;
    const nextStudy = caseQueueRef.current[0];

    if (previousStudy) {
      setLoadingQueue((prev) =>
        prev.filter((studyId) => studyId !== previousStudy.studyId)
      );
      console.log(`Removed study ${previousStudy.studyId} from loading queue`);
    }

    if (previousStudy && moveToProcessed) {
      setProcessedQueue((prev) => [...prev, previousStudy]);
    }

    setTransitionDirection("right");
    setPreviousCase(previousStudy);
    setIsTransitioning(true);

    setCaseQueue((prev) => prev.slice(1));
    setCurrentStudy(nextStudy);

    const currentIframeOrder = iframeOrderRef.current;
    const currentVisibleIndex = visibleIframeRef.current;
    const targetIframe = currentIframeOrder.findIndex(
      (id) => id === nextStudy.studyId
    );

    if (targetIframe !== -1) {
      console.log(`Next study ${nextStudy.studyId} found in iframe ${targetIframe}`);
      setVisibleIframe(targetIframe);

      if (previousStudy && currentVisibleIndex !== targetIframe) {
        const previousIframe = currentIframeOrder.findIndex(
          (id) => id === previousStudy.studyId
        );
        if (previousIframe !== -1 && previousIframe !== targetIframe) {
          console.log(`Clearing previous iframe ${previousIframe} with study ${previousStudy.studyId}`);

          setIframeOrder((prev) => {
            const newOrder = [...prev];
            newOrder[previousIframe] = -1;
            return newOrder;
          });

          if (iframeRefs[previousIframe].current) {
            iframeRefs[previousIframe].current.src = "about:blank";
          }

          setLoadingStates((prev) => ({ ...prev, [previousIframe]: false }));
        }
      }
    } else {
      console.log(`Next study ${nextStudy.studyId} not preloaded, loading now`);
      const targetSlot = currentVisibleIndex;

      if (iframeRefs[targetSlot].current) {
        iframeRefs[targetSlot].current.src = "about:blank";
      }

      setIframeOrder((prev) => {
        const newOrder = [...prev];
        newOrder[targetSlot] = nextStudy.studyId;
        return newOrder;
      });

      setCurrentlyLoading(nextStudy.studyId);
      loadStudyInIframe(targetSlot, nextStudy.studyId, true);
    }

    setTimeout(() => {
      setIsTransitioning(false);
      setPreviousCase(null);
    }, 800);
  };

  const handleIframeLoad = (iframeIndex) => {
    console.log("handle iframe load", iframeIndex);
    setLoadingStates((prev) => ({ ...prev, [iframeIndex]: false }));
  };

  const startNextStudyLoading = useCallback(() => {
    const currentLoadingRef = currentlyLoading;
    const currentQueueRef = loadingQueue;

    if (currentLoadingRef) {
      console.log(`Study ${currentLoadingRef} still loading, cannot start next`);
      return;
    }

    if (currentQueueRef.length === 0) {
      console.log("No studies in loading queue");
      return;
    }

    const nextStudyId = currentQueueRef[0];
    console.log("Starting to load next study:", nextStudyId);

    const currentAttempts = loadingAttempts[nextStudyId] || 0;
    if (currentAttempts >= MAX_LOADING_ATTEMPTS) {
      console.log(`Study ${nextStudyId} exceeded max attempts (${MAX_LOADING_ATTEMPTS}), marking as failed`);
      setFailedStudies((prev) => new Set([...prev, nextStudyId]));
      setLoadingQueue((prev) => prev.slice(1));
      return;
    }

    setLoadingAttempts((prev) => ({
      ...prev,
      [nextStudyId]: currentAttempts + 1,
    }));
    setCurrentlyLoading(nextStudyId);
    setLoadingQueue((prev) => prev.slice(1));

    const currentIframeOrder = iframeOrderRef.current;
    const currentVisibleIframe = visibleIframeRef.current;

    const freeSlot = currentIframeOrder.findIndex(
      (studyId, i) => studyId === -1 && i !== currentVisibleIframe
    );

    if (freeSlot !== -1) {
      console.log("Found free slot:", freeSlot, "for study:", nextStudyId);

      setIframeOrder((prev) => {
        const newOrder = [...prev];
        newOrder[freeSlot] = nextStudyId;
        return newOrder;
      });

      setTimeout(() => loadStudyInIframe(freeSlot, nextStudyId), 100);
    } else {
      const nonVisibleSlots = currentIframeOrder
        .map((studyId, index) => ({ studyId, index }))
        .filter(
          (slot) => slot.index !== currentVisibleIframe && slot.studyId !== -1
        );

      if (nonVisibleSlots.length > 0) {
        const evictSlot = nonVisibleSlots[0].index; 
        console.log(`Evicting iframe ${evictSlot} (had study ${currentIframeOrder[evictSlot]}) for study ${nextStudyId}`);

        if (iframeRefs[evictSlot].current) {
          iframeRefs[evictSlot].current.src = "about:blank";
        }

        setIframeOrder((prev) => {
          const newOrder = [...prev];
          newOrder[evictSlot] = nextStudyId;
          return newOrder;
        });

        setTimeout(() => loadStudyInIframe(evictSlot, nextStudyId), 200);
      } else {
        console.log("No free slot available for study:", nextStudyId);
        setCurrentlyLoading(null);
      }
    }
  }, [currentlyLoading, loadingQueue, loadingAttempts]);

  useEffect(() => {
    if (!currentlyLoading && loadingQueue.length > 0) {
      console.log("No study currently loading, starting next from queue...");
      const timer = setTimeout(() => {
        startNextStudyLoading();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [currentlyLoading, loadingQueue, startNextStudyLoading]);

  const onAllImagesLoaded = useCallback(
    (studyId) => {
      console.log("All images loaded for study:", studyId);

      setStudyCompletionStatus((prev) => ({ ...prev, [studyId]: true }));
      updatePrefetchStatus(studyId, "completed");
      setLoadingAttempts((prev) => {
        const newAttempts = { ...prev };
        delete newAttempts[studyId];
        return newAttempts;
      });

      setFailedStudies((prev) => {
        const newFailed = new Set(prev);
        newFailed.delete(studyId);
        return newFailed;
      });

      const iframeIndex = iframeOrderRef.current.findIndex(
        (id) => id === studyId
      );
      if (iframeIndex !== -1) {
        setLoadingStates((prev) => ({ ...prev, [iframeIndex]: false }));
      }

      if (!firstStudyFullyLoaded && currentStudyRef.current?.studyId === studyId) {
        console.log(`First study ${studyId} fully loaded! Setting flag to hide loading screen.`);
        setFirstStudyFullyLoaded(true);
      }

      if (currentlyLoading === studyId) {
        console.log(`Study ${studyId} completed! Clearing loading state and starting next...`);
        setCurrentlyLoading(null);
      }
    },
    [currentlyLoading, updatePrefetchStatus]
  );

  const handleCurrentStudyUpdate = (values, filterObj) => {
    setCurrentStudy((prev) => {
      let emergency = prev.emergency ?? 0;
      let nonMedicalEmergency = prev.nonMedicalEmergency ?? 0;

      if (filterObj.studyEmergency === 2) {
        emergency = 1;
        nonMedicalEmergency = 0;
      } else if (filterObj.studyEmergency === 3) {
        emergency = 1;
        nonMedicalEmergency = 1;
      } else {
        emergency = 0;
        nonMedicalEmergency = 0;
      }
      return{
        ...prev,
        patientId: values.patientId ?? prev.patientId,
        patientName: values.patientName ?? prev.patientName,
        description: values.studyDescription ?? prev.description,
        patientAge: values.age ?? prev.patientAge,
        patientSex: filterObj.sex ?? prev.patientSex,
        emergency: emergency,
        nonMedicalEmergency: nonMedicalEmergency,
      }
    });

    const updatedDescription = values.studyDescription;

    if (updatedDescription !== undefined) {
      const currentVisibleIndex = visibleIframeRef.current;
      const iframe = iframeRefs[currentVisibleIndex]?.current;

      if (iframe?.contentWindow?.updateStudyDescription) {
        iframe.contentWindow.updateStudyDescription(updatedDescription);
      } else {
        console.warn("Child iframe not ready yet, retrying...");
        setTimeout(() => {
          iframe?.contentWindow?.updateStudyDescription?.(updatedDescription);
        }, 100);
      }
    }
  }

  const logBlitzSessionClose = async (reason) => {
    if (isSessionClosedRef.current) return;
    isSessionClosedRef.current = true;

    try {
      closeBlitzSession(sessionIdRef.current, reason);
      console.log(`Blitz session closed: ${reason}`);
    } catch (err) {
      console.error("Failed to log blitz session close:", err);
    }
  };

  window.goToNextStudy = goToNextStudy;
  window.onAllImagesLoaded = onAllImagesLoaded;

  return (
    <blitzSessionContext.Provider
      value={{
        caseQueue,
        setCaseQueue,
        processedQueue,
        setProcessedQueue,
        sessionId,
        setSessionId,
        showCloseBlitzPopup, 
        setShowCloseBlitzPopup,
        isLoading,
        setIsLoading,
        loadingError,
        setLoadingError,
        isDarkTheme,
        setIsDarkTheme,
        isTransitioning,
        setIsTransitioning,
        transitionDirection,
        setTransitionDirection,
        previousCase,
        setPreviousCase,
        iframeRefs,
        currentIndex,
        setCurrentIndex,
        visibleIframe,
        setVisibleIframe,
        iframeOrder,
        setIframeOrder,
        loadingStates,
        setLoadingStates,
        firstStudyLoaded,
        setFirstStudyLoaded,
        goToNextStudy,
        currentStudy,
        loadStudyInIframe,
        getStudyUrl,
        handleIframeLoad,
        onAllImagesLoaded,
        showCaseRemovedPopup,
        setShowCaseRemovedPopup,
        handleCloseRemovedCasePopup,
        removedCaseInfo,
        setRemovedCaseInfo,
        currentlyLoading,
        studyCompletionStatus,
        updatePrefetchStatus,
        isQueueRefreshingRef,
        caseQueueRef,
        loadingQueue,
        handleCurrentStudyUpdate,
        showEmptyQueuePopup, 
        setShowEmptyQueuePopup,
        firstStudyFullyLoaded,
        setFirstStudyFullyLoaded,
        studyBlitzFilter, setStudyBlitzFilter,
        logBlitzSessionClose,
      }}
    >
      {props.children}
    </blitzSessionContext.Provider>
  );
};

export default BlitzSessionState;